import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import Alert from "../../../Common/Alert";
import DatatableFunc from "../../../Common/DatatableFunc";
import Http from "../../../Common/Http";
import Swal from "sweetalert2";
import IsLogin from "../../../Common/IsLogin";
import { ProgressSpinner } from "primereact/progressspinner";
import ReportDateRangeCsv from "../../../Common/ReportDateRangeCSV";

const DecorationExportReport = () => {
  const location = useLocation();
  const [receiveData, setReceiveData] = useState([]);
  const [alert, setAlert] = useState(null);
  const date = new Date();
  const month = date.getMonth() + 1;
  const year = date.getFullYear();
  const [inputs, setInputs] = useState({
    date_from: getCurrentDate(),
    date_to: getCurrentDate(),
  });
  const [loading, setLoading] = useState(false);

  const repairTypes = ["PH", "SS", "AIR", "HC"];

  function getCurrentDate() {
    const dateObj = new Date();
    const month = ("0" + (dateObj.getMonth() + 1)).slice(-2);
    const day = ("0" + dateObj.getDate()).slice(-2);
    const year = dateObj.getFullYear();
    return `${year}-${month}-${day}`;
  }

  const handleChange = (e) => {
    const name = e.target.name;
    const value = e.target.value;
    setInputs((values) => ({ ...values, [name]: value }));
  };

  const fetchData = (dateFromParam, dateToParam) => {
    const dateFrom = dateFromParam || inputs.date_from || getCurrentDate();
    const dateTo = dateToParam || inputs.date_to || getCurrentDate();
    setLoading(true);
    Http.get(
      `/production-and-transfer-list?date_from=${dateFrom}&date_to=${dateTo}`,
    )
      .then((res) => {
        setReceiveData(res.data);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    DatatableFunc();
    fetchData();
  }, []);

  const showAlert = (message, type) => {
    setAlert({
      message: message,
      type: type,
    });
    setTimeout(() => {
      setAlert(null);
    }, 4000);
  };

  useEffect(() => {
    if (!location.state) {
      return;
    }
    showAlert(location.state.message, location.state.type);
  }, [location]);

  const formSubmit = () => {
    fetchData(inputs.date_from, inputs.date_to);
  };

  const calculateRowValues = (item) => {
    const receiveDataArr = item.receive_data || [];
    const productionData = item.production_data || [];
    const transferData = item.transfer_data || [];

    const receivedExport = receiveDataArr.reduce(
      (sum, r) => sum + Number(r.receive_qty_export || 0),
      0,
    );

    // const producedExport = productionData.reduce(
    //   (sum, p) => sum + Number(p.qty_export || 0),
    //   0
    // );

    const transferredExport = transferData.reduce(
      (sum, t) =>
        sum +
        (t.is_return === 0 || t.is_return === undefined
          ? Number(t.delivery_qty_export || 0)
          : 0),
      0,
    );

    const lossExport = productionData.reduce(
      (sum, p) => sum + Number(p.loss_qty_export || 0),
      0,
    );

    const repairTransferData = item.repair_transfer_data || [];
    const repairReceiveData = item.repair_receive_data || [];

    const repairCounts = repairTypes.reduce((acc, type) => {
      const fromTransfer = repairTransferData.reduce((sum, r) => {
        if (r.type_heading === type) {
          const qty =
            r.delivery_qty != null
              ? Number(r.delivery_qty)
              : Number(r.receive_qty || 0);
          return sum + qty;
        }
        return sum;
      }, 0);

      const fromReceive = repairReceiveData.reduce((sum, r) => {
        if (r.type_heading === type) {
          const qty =
            r.receive_qty != null
              ? Number(r.receive_qty)
              : Number(r.delivery_qty || 0);
          return sum + qty;
        }
        return sum;
      }, 0);

      acc[type] = fromTransfer + fromReceive;
      return acc;
    }, {});

    return {
      receivedExport,
      // producedExport,
      transferredExport,
      lossExport,
      repairCounts,
    };
  };

  const totals = receiveData.reduce(
    (acc, item) => {
      const {
        receivedExport,
        // producedExport,
        transferredExport,
        lossExport,
        repairCounts,
      } = calculateRowValues(item);

      const repairTotals = { ...acc.repairTotals };
      repairTypes.forEach((type) => {
        repairTotals[type] =
          (repairTotals[type] || 0) + (repairCounts[type] || 0);
      });

      return {
        totalReceivedExport: acc.totalReceivedExport + receivedExport,
        // totalProducedExport: acc.totalProducedExport + producedExport,
        totalTransferredExport: acc.totalTransferredExport + transferredExport,
        totalLossExport: acc.totalLossExport + lossExport,
        repairTotals,
      };
    },
    {
      totalReceivedExport: 0,
      // totalProducedExport: 0,
      totalTransferredExport: 0,
      totalLossExport: 0,
      repairTotals: repairTypes.reduce((obj, type) => {
        obj[type] = 0;
        return obj;
      }, {}),
    },
  );

  const getCsvData = () => {
    const headers = [
      "Name of Ware",
      "Shape",
      "Glaze",
      "Decoration",
      "Received Export",
      "Transferred Export",
      "Loss Export",
      ...repairTypes,
    ];

    const rows = receiveData.map((item) => {
      const { receivedExport, transferredExport, lossExport, repairCounts } =
        calculateRowValues(item);
      return [
        item.product_name,
        item.shape,
        item.glaze,
        `${item.decoration_code} (${item.decoration_name})`,
        receivedExport,
        transferredExport,
        lossExport,
        ...repairTypes.map((type) => repairCounts[type] || 0),
      ];
    });

    const footerRows = [
      [
        "",
        "",
        "",
        "Total",
        totals.totalReceivedExport,
        totals.totalTransferredExport,
        totals.totalLossExport,
        ...repairTypes.map((type) => totals.repairTotals[type]),
      ],
    ];

    return { headers, rows, footerRows };
  };

  return (
    <div>
      <IsLogin></IsLogin>
      <div>
        <div className="am-mainpanel">
          <div className="am-pagetitle">
            <h5 className="am-title">Decoration Export Report</h5>
          </div>
          <div className="am-pagebody">
            <Alert alert={alert} />
            <div className="card pd-20 pd-sm-40">
              <ReportDateRangeCsv
                dateFrom={inputs.date_from}
                dateTo={inputs.date_to}
                onChange={handleChange}
                onSearch={formSubmit}
                csvFilename={`decoration-export-report_${inputs.date_from}_to_${inputs.date_to}.csv`}
                getCsvData={getCsvData}
              />
            </div>
          </div>
          <div className="am-pagebody">
            <div className="card pd-20 pd-sm-40">
              <div className="row">
                <div className="col-12">
                  <div className="text-center">
                    <h5 className="text-uppercase text-sm font-weight-bolder">
                      Artisan Ceramics Limited
                    </h5>
                    <p>
                      House-16, Road-07, Sector-01, Uttara, Dhaka
                      <br />
                      <b>Decoration Export Report</b>
                    </p>
                  </div>
                  <div
                    className="table-responsive p-0 pb-2"
                    style={{ maxHeight: "70vh", overflowY: "auto" }}
                  >
                    <table className="table table-sm table-bordered table-striped align-items-center justify-content-center mb-0 _datatable">
                      <thead
                        style={{
                          position: "sticky",
                          top: 0,
                          zIndex: 2,
                          backgroundColor: "#fff",
                        }}
                      >
                        <tr>
                          <th
                            className="text-uppercase text-dark text-sm font-weight-bolder ps-2"
                            rowSpan={2}
                          >
                            Name of Ware
                          </th>
                          <th
                            className="text-uppercase text-dark text-sm font-weight-bolder ps-2 text-center"
                            rowSpan={2}
                          >
                            Shape
                          </th>
                          <th
                            className="text-uppercase text-dark text-sm font-weight-bolder ps-2 text-center"
                            rowSpan={2}
                          >
                            Glaze
                          </th>
                          <th
                            className="text-uppercase text-dark text-sm font-weight-bolder ps-2 text-center"
                            rowSpan={2}
                          >
                            Decoration
                          </th>
                          {/* <th
                            className="text-uppercase text-dark text-sm font-weight-bolder ps-2 text-center"
                            rowSpan={2}
                          >
                            Decoration Name
                          </th> */}
                          <th
                            className="text-uppercase text-dark text-sm font-weight-bolder ps-2 text-center"
                            rowSpan={2}
                          >
                            Received Export
                          </th>
                          {/* <th
                            className="text-uppercase text-dark text-sm font-weight-bolder ps-2 text-center"
                            rowSpan={2}
                          >
                            Produced Export
                          </th> */}
                          <th
                            className="text-uppercase text-dark text-sm font-weight-bolder ps-2 text-center"
                            rowSpan={2}
                          >
                            Transferred Export
                          </th>
                          <th
                            className="text-uppercase text-dark text-sm font-weight-bolder ps-2 text-center"
                            rowSpan={2}
                          >
                            Loss Export
                          </th>
                          <th
                            className="text-uppercase text-dark text-sm font-weight-bolder ps-2 text-center"
                            colSpan={repairTypes.length}
                          >
                            Repairs
                          </th>
                        </tr>
                        <tr>
                          {repairTypes.map((type) => (
                            <th
                              key={type}
                              className="text-uppercase text-dark text-sm font-weight-bolder ps-2 text-center"
                            >
                              {type}
                            </th>
                          ))}
                        </tr>
                      </thead>

                      <tbody>
                        {loading ? (
                          <tr>
                            <td
                              className="text-center"
                              colSpan={12}
                              style={{ padding: "2rem" }}
                            >
                              <ProgressSpinner
                                style={{ width: "40px", height: "40px" }}
                                strokeWidth="4"
                              />
                            </td>
                          </tr>
                        ) : receiveData && receiveData.length > 0 ? (
                          receiveData.map((item, index) => {
                            const {
                              receivedExport,
                              // producedExport,
                              transferredExport,
                              lossExport,
                              repairCounts,
                            } = calculateRowValues(item);

                            return (
                              <tr key={index}>
                                <td className="text-uppercase text-dark text-sm font-weight-bolder ps-2">
                                  {item.product_name}
                                </td>
                                <td className="text-uppercase text-dark text-sm font-weight-bolder ps-2">
                                  {item.shape}
                                </td>
                                <td className="text-uppercase text-dark text-sm font-weight-bolder ps-2">
                                  {item.glaze}
                                </td>
                                <td className="text-uppercase text-dark text-sm font-weight-bolder ps-2">
                                  {item.decoration_code} ({item.decoration_name}
                                  )
                                </td>
                                {/* <td className="text-uppercase text-dark text-sm font-weight-bolder ps-2">
                                  {item.decoration_name}
                                </td> */}
                                <td className="text-uppercase text-dark text-sm font-weight-bolder ps-2">
                                  {receivedExport}
                                </td>
                                {/* <td className="text-uppercase text-dark text-sm font-weight-bolder ps-2">
                                  {producedExport}
                                </td> */}
                                <td className="text-uppercase text-dark text-sm font-weight-bolder ps-2">
                                  {transferredExport}
                                </td>
                                <td className="text-uppercase text-dark text-sm font-weight-bolder ps-2">
                                  {lossExport}
                                </td>
                                {repairTypes.map((type) => (
                                  <td
                                    key={type}
                                    className="text-uppercase text-dark text-sm font-weight-bolder ps-2"
                                  >
                                    {repairCounts[type] || 0}
                                  </td>
                                ))}
                              </tr>
                            );
                          })
                        ) : (
                          <tr>
                            <td
                              className="text-uppercase text-dark text-sm font-weight-bolder ps-2 text-center"
                              colSpan={5 + 4 + repairTypes.length}
                            >
                              No data found
                            </td>
                          </tr>
                        )}
                      </tbody>
                      <tfoot>
                        <tr>
                          <td
                            className="text-uppercase text-dark text-sm font-weight-bolder ps-2"
                            colSpan={4}
                          >
                            Total
                          </td>
                          <td className="text-uppercase text-dark text-sm font-weight-bolder ps-2">
                            {totals.totalReceivedExport}
                          </td>
                          {/* <td className="text-uppercase text-dark text-sm font-weight-bolder ps-2">
                            {totals.totalProducedExport}
                          </td> */}
                          <td className="text-uppercase text-dark text-sm font-weight-bolder ps-2">
                            {totals.totalTransferredExport}
                          </td>
                          <td className="text-uppercase text-dark text-sm font-weight-bolder ps-2">
                            {totals.totalLossExport}
                          </td>
                          {repairTypes.map((type) => (
                            <td
                              key={type}
                              className="text-uppercase text-dark text-sm font-weight-bolder ps-2"
                            >
                              {totals.repairTotals[type]}
                            </td>
                          ))}
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DecorationExportReport;
